import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import useAuth from '@contexts/useAuth';
import useSnackbar from '@hooks/useSnackbar';
import { getProfile, updateDisplayName } from '@api/profile';
import type { ProfileResponse } from '@api/profile';
import PageHeader, {
  PAGE_HEADER_VARIANT_SMALL,
} from '@components/2-molecules/PageHeader';
import SettingInfo from '@components/2-molecules/SettingInfo';
import HorizontalDivider from '@components/1-atoms/HorizontalDivider';
import Button, { BUTTON_VARIANT_PRIMARY, BUTTON_VARIANT_TEXT } from '@components/1-atoms/Button';
import EulaPdfModal from '@components/EulaPdfModal/EulaPdfModal';
import { EULA_URL } from '@constants/index';
import { Frame } from '../Settings.styled';
import { Wrapper, NameEditRow, NameInput } from './AccountSettings.styled';

const PROFILE_QUERY_KEY = ['profile'];

const AccountSettings: React.FC = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const [showEulaModal, setShowEulaModal] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useSnackbar();

  const profileQuery = useQuery<ProfileResponse>({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: () => getProfile(user!.token),
    enabled: !!user?.token,
  });

  const email = profileQuery.data?.email ?? '';

  const [syncedServerName, setSyncedServerName] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  if (profileQuery.data && syncedServerName !== profileQuery.data.displayName) {
    setSyncedServerName(profileQuery.data.displayName);
    setDisplayName(profileQuery.data.displayName);
    setOriginalName(profileQuery.data.displayName);
  }

  useEffect(() => {
    if (profileQuery.isError) {
      showError('Failed to load profile');
    }
  }, [profileQuery.isError, showError]);

  const nameMutation = useMutation({
    mutationFn: (trimmedName: string) => updateDisplayName(user!.token, trimmedName),
    onSuccess: (result) => {
      queryClient.setQueryData<ProfileResponse>(PROFILE_QUERY_KEY, result);
      setOriginalName(result.displayName);
      setDisplayName(result.displayName);
      showSuccess(intl.formatMessage({
        id: 'settings.account.name.updated',
        defaultMessage: 'Name updated successfully',
      }));
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to update name';
      showError(msg);
    },
  });

  const hasNameChanged = displayName.trim() !== originalName;

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDisplayName(value);
    if (value.trim().length < 1) {
      setNameError('Name is required');
    } else if (value.trim().length > 100) {
      setNameError('Name must be 100 characters or less');
    } else {
      setNameError(null);
    }
  }, []);

  const handleSaveName = useCallback(() => {
    if (!user?.token || !hasNameChanged || nameError) return;
    nameMutation.mutate(displayName.trim());
  }, [user?.token, displayName, hasNameChanged, nameError, nameMutation]);

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && hasNameChanged && !nameError) {
        handleSaveName();
      }
    },
    [hasNameChanged, nameError, handleSaveName],
  );

  if (profileQuery.isLoading) {
    return (
      <Wrapper>
        <PageHeader
          variant={PAGE_HEADER_VARIANT_SMALL}
          title={intl.formatMessage({ id: 'settings.account.profile', defaultMessage: 'Profile' })}
        />
        <Frame>
          <SettingInfo
            label={intl.formatMessage({ id: 'settings.account.loading', defaultMessage: 'Loading...' })}
          />
        </Frame>
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid="account-settings">
      <PageHeader
        dataTestId="settings-page__profile-header"
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage({ id: 'settings.account.profile', defaultMessage: 'Profile' })}
      />
      <Frame>
        <SettingInfo
          dataTestId="settings-page__profile__name"
          label={intl.formatMessage({ id: 'settings.account.name', defaultMessage: 'Name' })}
          infoText={hasNameChanged ? undefined : displayName}
        />
        {hasNameChanged ? (
          <NameEditRow>
            <NameInput
              type="text"
              value={displayName}
              onChange={handleNameChange}
              onKeyDown={handleNameKeyDown}
              placeholder="Your name"
              maxLength={100}
            />
            <Button
              dataTestId="settings-page__profile__save-name"
              variant={BUTTON_VARIANT_PRIMARY}
              onClick={handleSaveName}
              disabled={nameMutation.isPending || !!nameError}
              stretch={false}
            >
              {nameMutation.isPending
                ? intl.formatMessage({ id: 'settings.account.saving', defaultMessage: 'Saving...' })
                : intl.formatMessage({ id: 'settings.account.save', defaultMessage: 'Save' })}
            </Button>
            <Button
              variant={BUTTON_VARIANT_TEXT}
              onClick={() => {
                setDisplayName(originalName);
                setNameError(null);
              }}
              stretch={false}
            >
              {intl.formatMessage({ id: 'settings.account.cancel', defaultMessage: 'Cancel' })}
            </Button>
          </NameEditRow>
        ) : null}

        <HorizontalDivider middleInset />

        <SettingInfo
          dataTestId="settings-page__profile__email"
          label={intl.formatMessage({ id: 'settings.account.email', defaultMessage: 'Email' })}
          infoText={email}
        />

        <HorizontalDivider middleInset />

        <Button
          dataTestId="settings-page__account__view-eula"
          variant={BUTTON_VARIANT_TEXT}
          onClick={() => setShowEulaModal(true)}
          stretch={false}
        >
          {intl.formatMessage({
            id: 'settings.account.viewEula',
            defaultMessage: 'View License Agreement',
          })}
        </Button>

        <HorizontalDivider middleInset />

        <Button
          dataTestId="settings-page__account__logout"
          variant={BUTTON_VARIANT_TEXT}
          onClick={logout}
          stretch={false}
        >
          {intl.formatMessage({ id: 'settings.account.logout', defaultMessage: 'Logout' })}
        </Button>
      </Frame>

      {showEulaModal && (
        <EulaPdfModal url={EULA_URL} onClose={() => setShowEulaModal(false)} />
      )}
    </Wrapper>
  );
};

export default AccountSettings;
